import { Logger, loggerManager } from '../index.js';
import { isFunction, performanceNow, normalizeUrl } from '../utils/utils';
import { addPromiseRetry } from '../utils/decorators/promise-retry';
import kvStatManager, { KV_REPORT_KEY_DOWNLOAD } from '../manager/kv-stat-manager';

const isDataURI = (filename: string) => filename.startsWith('data:application/octet-stream;base64,');
const isFileURI = (filename: string) => filename.startsWith('file://');

type ResponseType = Blob | ArrayBuffer | WebAssembly.Instance;

class FileDownloader {
  private _log: Logger;

  constructor() {
    this._log = loggerManager.createLogger({
      id: 'fd'
    });
  }

  @addPromiseRetry({
    settings: { timeout: 0, retries: 3 },
    onError(this: FileDownloader, error, retry, reject) {
      if (error?.status === 404 || error?.message?.includes('404')) {
        this._log.warn('download 404, stop retry');
        reject(error);
      } else {
        retry();
      }
    },
    onRetrying(this: FileDownloader, retryCount) {
      this._log.warn(`download retrying: ${retryCount}`);
    }
  })
  async download(path: string, options?: { retries?: number, type?: 'blob' | 'arraybuffer'; }): Promise<ResponseType> {
    const { type = 'blob' } = options || {};
    // 规范化URL路径: 保留协议部分的双斜杠，替换路径中的连续斜杠为单斜杠
    path = normalizeUrl(path);

    try {
      const now = performanceNow();
      let result: { data: ResponseType; };
      if (isFunction(fetch)) {
        result = await this.downloadWithFetch(path, type);
      } else {
        result = await this.downloadWithXHR(path, type);
      }
      if (!result || !result.data) {
        throw new Error('data is empty');
      }

      const cost = performanceNow() - now;
      this._log.info(`downloaded: ${path}, return type: ${type}, cost: ${cost}ms`);
      kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_DOWNLOAD.DOWNLOAD, cost: performanceNow() - now });

      return result.data;
    } catch (error: any) {
      this._log.error(`failed to download: ${path}, error: ${error}`);
      kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_DOWNLOAD.DOWNLOAD, error });
      throw error;
    }
  }

  async downloadWithFetch(path: string, type: string): Promise<{ data: ResponseType; }> {
    this._log.info(`download with fetch: ${path}, return type: ${type}`);
    try {
      const response = await fetch(path);
      if (!response.ok) {
        const err: any = new Error(`network response was not ok: ${response.status}`);
        err.status = response.status;
        throw err;
      }
      let result: ResponseType;
      if (type === 'arraybuffer') {
        result = await response.arrayBuffer();
      } else { // default blob
        result = await response.blob();
      }
      return { data: result };
    } catch (error) {
      throw error;
    }
  }

  downloadWithXHR(path: string, type: XMLHttpRequestResponseType): Promise<{ data: ResponseType; }> {
    this._log.info(`download with xhr: ${path}, return type: ${type}`);
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', path, true);
      xhr.responseType = type;
      xhr.onload = () => {
        if (xhr.status === 200 || (xhr.status === 0 && xhr.response)) {
          resolve({ data: xhr.response });
        } else {
          const err: any = new Error(`XHR failed, status: ${xhr.status}`);
          err.status = xhr.status;
          reject(err);
        }
      };
      xhr.onerror = reject;
      xhr.send(null);
    });
  }

  async loadWasm(source: string, importObject?: WebAssembly.Imports) {
    this._log.info(`loadWasm ${source}, importObject: ${JSON.stringify(importObject)}`);

    const now = performanceNow();
    let instance: WebAssembly.Instance | null = null;
    let error: any = null;

    if (isFunction(WebAssembly.instantiateStreaming) && !isDataURI(source) && !isFileURI(source) && isFunction(fetch)) {
      try {
        const response = fetch(source);
        const result = await WebAssembly.instantiateStreaming(response, importObject);
        instance = result.instance;
      } catch (e) {
        error = e;
      }
    }

    if (!instance) {
      try {
        const arrayBuffer = await this.download(source, { type: 'arraybuffer' }) as ArrayBuffer;
        const result = await WebAssembly.instantiate(arrayBuffer, importObject);
        instance = result.instance;
      } catch (e) {
        error = e;
      }
    }

    if (instance) {
      const cost = performanceNow() - now;
      this._log.info(`loadedWasm ${source}, cost: ${cost}ms`);
      kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_DOWNLOAD.LOAD_WASM, cost });
      return instance;
    }

    this._log.error(`failed to loadWasm ${source}, error: ${error}`);
    kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_DOWNLOAD.LOAD_WASM, error });
    throw error;
  }

  @addPromiseRetry({
    settings: { timeout: 3000, retries: 3 },
    onRetrying(this: FileDownloader, retryCount) {
      this._log.warn(`loadScript retrying: ${retryCount}`);
    }
  })
  loadScript(src: string) {
    this._log.info(`loadScript ${src}`);
    const now = performanceNow();
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.onload = () => {
        this._log.info(`loadedScript ${src}, cost: ${performanceNow() - now}ms`);
        kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_DOWNLOAD.LOAD_SCRIPT, cost: performanceNow() - now, split: 1000 });
        resolve(script);
      };
      script.onerror = e => {
        this._log.error(`failed to loadScript ${src}, error: ${(e as any)?.message || JSON.stringify(e)}`);
        kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_DOWNLOAD.LOAD_SCRIPT });
        reject(e);
      };
      script.crossOrigin = 'anonymous';
      script.src = src;
      if (document.head.append) {
        document.head.append(script);
      } else {
        document.getElementsByTagName('head')[0].appendChild(script);
      }
    });
  }
}

export { FileDownloader };

export default new FileDownloader();
